import React, { useState } from "react";
import {
  Table,
  Header,
  CollectionPreferences,
  Pagination,
  PropertyFilter,
  TableProps,
  CollectionPreferencesProps,
  Box,
  ExpandableSection,
} from "@amzn/awsui-components-react";
import { useCollection } from "@amzn/awsui-collection-hooks";
import { PROPERTY_FILTERING_I18N_CONSTANTS } from "../../utils/tableConfig";
import "./recostReportTable.css";

/**
 * Table component for displaying the information about each region in the given realm.
 *
 * @param itemList - list of items with contents to display in the table
 * @param columnDefinitions - header definitions for the table columns
 * @param visibleContentOptions - defines the toggleable column settings
 * @param fetchingData - true if data for the table is pending from another source
 * @param pageSizeOptions - options for the page size settings
 * @param filterProperties - filter properties for each column which enable complex filtering
 * @param preferences - table preference settings
 * @param setPreferences - set function for modification of preferences
 * @param tableName - name of the table
 * @param nestedTableColumnDefinitions - column definitions for nested tables
 * @return <ComplexFilterTable
 *              itemList={itemList}
 *              columnDefinitions={columnDefinitions}
 *              visibleContentOptions={visibleContentOptions}
 *              fetchingData={fetchingData}
 *              pageSizeOptions={pageSizeOptions}
 *              filterProperties={filterProperties}
 *              preferences={preferences}
 *              setPreferences={setPreferences}
 *              tableName={tableName}
 *              nestedTableColumnDefinitions={nestedTableColumnDefinitions}
 *         />
 */
const ComplexFilterTable = ({
  itemList,
  columnDefinitions,
  visibleContentOptions,
  fetchingData,
  pageSizeOptions,
  filterProperties,
  preferences,
  setPreferences,
  tableName,
  nestedTableColumnDefinitions,
  reportLayout = false,
  tableDescription,
}: {
  itemList: any[];
  columnDefinitions: TableProps.ColumnDefinition<any>[];
  visibleContentOptions: CollectionPreferencesProps.VisibleContentOptionsGroup[];
  fetchingData: boolean;
  pageSizeOptions: CollectionPreferencesProps.PageSizeOption[];
  filterProperties: any;
  preferences: any;
  setPreferences: any;
  tableName: any;
  nestedTableColumnDefinitions?: TableProps.ColumnDefinition<any>[];
  /** Report-only layout: wrapped, resizable columns with the first visible column pinned. */
  reportLayout?: boolean;
  tableDescription?: React.ReactNode;
}) => {
  const {
    items,
    filteredItemsCount,
    collectionProps,
    paginationProps,
    propertyFilterProps,
  } = useCollection(itemList, {
    propertyFiltering: {
      filteringProperties: filterProperties,
    },
    sorting: {},
    pagination: { pageSize: preferences.pageSize },
  });

  return (
    <div>
      <Table
        {...collectionProps}
        className={reportLayout ? "recost-report-table" : undefined}
        header={
          <Header counter={`(${itemList.length})`} description={tableDescription}>
            {tableName}
          </Header>
        }
        columnDefinitions={columnDefinitions}
        visibleColumns={preferences.visibleContent}
        items={items}
        loading={fetchingData}
        loadingText="Fetching Table Data"
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            i18nStrings={PROPERTY_FILTERING_I18N_CONSTANTS}
            countText={
              filteredItemsCount !== itemList.length
                ? `(${filteredItemsCount} / ${itemList.length})`
                : `(${itemList.length})`
            }
            expandToViewport={true}
          />
        }
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title={tableName}
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={{
              title: "Page Size",
              options: pageSizeOptions,
            }}
            visibleContentPreference={{
              title: "Select Visible Columns",
              options: visibleContentOptions,
            }}
          />
        }
        variant="container"
        stickyHeader={true}
        resizableColumns={reportLayout}
        wrapLines={reportLayout}
        ariaLabels={reportLayout ? { tableLabel: tableName } : undefined}
      />
      
      {nestedTableColumnDefinitions && items.map((item, index) => {
        const originalIndex = itemList.findIndex(originalItem => 
          originalItem.rsmVersion === item.rsmVersion && 
          originalItem.stateID === item.stateID
        );
        return(
        <ExpandableSection 
          key={`${item.rsmVersion}-${item.stateID}`}
          headerText={`Details for MapPack ${itemList[originalIndex].rsmVersion}_${itemList[originalIndex].stateID}`}
          variant="container"
        >
          <Box padding="s">
            <Table
              columnDefinitions={nestedTableColumnDefinitions}
              items={item.nestedItems || []}
              variant="embedded"
              header={<Header>Related Items</Header>}
            />
          </Box>
        </ExpandableSection>)
      })}
    </div>
  );
};

export default ComplexFilterTable;
